import { usePublishedData } from "../lib/published-data";
import { useEffect } from "react";
import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router";
import contentStyles from "../styles/content.css?url";
import { PostsFeed } from "../components/content/posts-feed";
import { DocumentContent } from "../components/document-content";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { useShell } from "../lib/document-route";
import { nativeFeatureQuery } from "../lib/links";
import { keepSearch, searchParams, type RawSearch } from "../lib/search";
import { postFeedFilters } from "../../../shared/src/post-feed";
import { reusableFeed } from "../lib/reusable-feed";
import { loadNews } from "../server/content";

type Feed = Extract<Awaited<ReturnType<typeof loadNews>>, { kind: "feed" }>;

const feed = reusableFeed<Feed>();

/**
 * News (/blog): the page the creator composed, or the native feed. Searches, sorts and filters
 * show the feed and narrow it in the browser.
 */
export const Route = createFileRoute("/blog/")({
  // The address stays as written (campaign tags too); the feed reads the choices it knows.
  validateSearch: keepSearch,
  // Only moving between the composed page and the feed asks for new data.
  loaderDeps: ({ search }) => ({ native: nativeFeatureQuery("/blog", searchParams(search)) }),
  loader: async ({ deps }) => {
    const kept = feed.get();
    if (kept) return kept;
    const data = await loadNews({ data: deps.native });
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head =
      loaderData.kind === "page"
        ? creatorPageHead(shell, {
            pathname: "/blog",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
            preloadImage: loaderData.lead,
          })
        : creatorPageHead(shell, {
            pathname: "/blog",
            title: `${loaderData.title} | ${shell.creator.name}`,
            description: loaderData.intro || shell.creator.description,
          });
    return withStylesheet(head, contentStyles);
  },
  component: News,
});

function News() {
  const initial = Route.useLoaderData();
  const search = Route.useSearch();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/blog", "page");
  const navigate = useNavigate({ from: "/blog/" });
  useEffect(() => {
    if (initial.kind === "feed") feed.keep(initial);
  }, [initial]);
  if (data.kind === "page") return <DocumentContent data={data} shell={shell} />;
  return (
    <PostsFeed
      feed={data}
      ownerPost={shell.owner?.post}
      filters={postFeedFilters(searchParams(search))}
      onSearch={(q) =>
        void navigate({
          search: (previous: RawSearch) => ({ ...previous, q: q || undefined }),
          resetScroll: false,
        })
      }
    />
  );
}
